import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';
import styles from './ContentDrill.module.scss';
import classNames from 'classnames';
import { Button, useMediaQuery } from '@mui/material';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import AccountIcon from './AccountIcon';
import SearchBar from '../atoms/SearchBar';

export interface ContentDrillActionsItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

export interface ContentDrillProps {
  title?: string;
  showHeader?: boolean; // defaults to true at root, false for nested when using context
  onBack?: () => void;
  actions?: ContentDrillActionsItem[];
  searchPlaceholder?: string;
  searchQuery?: string;
  setSearchQuery?: (q: string) => void;
  disableSearch?: boolean;
  children?: React.ReactNode; // main content
  drillInContent?: React.ReactNode; // secondary pane content
  isDrilledIn?: boolean; // control whether to show secondary pane
  isParent?: boolean; // add outer padding when true
}

// Header context to avoid nested headers and allow nested screens to override header
type HeaderOverride = {
  title?: string;
  onBack?: () => void;
  actions?: ContentDrillActionsItem[];
};

type HeaderContextType = {
  hasHeader: boolean;
  setHeaderOverride?: (override: HeaderOverride | null) => void;
} | null;

const HeaderContext = createContext<HeaderContextType>(null);

export const useContentDrillHeader = () => useContext(HeaderContext);

const ContentDrill: React.FC<ContentDrillProps> = ({
  title,
  onBack,
  actions = [],
  searchPlaceholder,
  searchQuery,
  setSearchQuery,
  disableSearch,
  children,
  drillInContent,
  isDrilledIn,
  showHeader,
  isParent,
}) => {
  const parentHeader = useContentDrillHeader();
  const [headerOverride, setHeaderOverride] = useState<HeaderOverride | null>(
    null,
  );
  const viewportRef = useRef<HTMLDivElement>(null);
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  // Measure viewport width to drive precise sliding widths via CSS var
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const setWidthVar = () => {
      const w = el.getBoundingClientRect().width;
      el.style.setProperty('--cd-width', `${w}px`);
    };
    setWidthVar();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => setWidthVar());
      ro.observe(el);
    }
    const onResize = () => setWidthVar();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  // If parent already provided a header, nested instances hide their own header by default
  const effectiveShowHeader = useMemo(() => {
    if (typeof showHeader === 'boolean') return showHeader;
    return parentHeader ? false : true;
  }, [showHeader, parentHeader]);

  const placeholder = searchPlaceholder || 'Search...';
  const query = searchQuery || '';
  const handleSearchQuery = setSearchQuery || (() => {});

  const headerValue = useMemo(() => {
    if (effectiveShowHeader) {
      return { hasHeader: true, setHeaderOverride } as const;
    }
    // Propagate parent's controller so nested content can still override top header
    return parentHeader || { hasHeader: false };
  }, [effectiveShowHeader, parentHeader]);

  return (
    <HeaderContext.Provider value={headerValue}>
      <div
        className={classNames(styles.contentDrill, {
          [styles.padded]: !!isParent,
        })}
      >
        {effectiveShowHeader && (
          <div className={styles.header}>
            <div className={styles.titleWrapper}>
              {(headerOverride?.onBack || onBack) && (
                <Button
                  onClick={headerOverride?.onBack || onBack}
                  className={styles.backButton}
                >
                  <ArrowBackIosNewOutlinedIcon
                    style={{ color: 'var(--color-text-3)' }}
                  />
                </Button>
              )}
              <span className={styles.title}>
                {headerOverride?.title || title}
              </span>
            </div>
            {!isSmallScreen && <AccountIcon />}
          </div>
        )}

        {!isDrilledIn && (actions.length > 0 || !disableSearch) && (
          <div className={styles.actionsRow}>
            {!disableSearch && (
              <SearchBar
                placeholder={placeholder}
                searchQuery={query}
                setSearchQuery={handleSearchQuery}
              />
            )}
            <div className={styles.actionItems}>
              {actions.map(({ label, icon, onClick }) => (
                <Button
                  key={label}
                  className={styles.actionBtn}
                  onClick={onClick}
                >
                  {icon}
                  <span>{label}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {headerOverride?.actions && headerOverride.actions.length > 0 && (
          <div className={styles.actionsRow}>
            <div className={styles.actionItems}>
              {headerOverride.actions.map(({ label, icon, onClick }) => (
                <Button
                  key={label}
                  className={styles.actionBtn}
                  onClick={onClick}
                >
                  {icon}
                  <span>{label}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.viewport} ref={viewportRef}>
          <div
            className={classNames(styles.panePrimary, {
              [styles.shifted]: isDrilledIn,
            })}
          >
            {children}
          </div>
          <div
            className={classNames(styles.paneSecondary, {
              [styles.visible]: isDrilledIn,
            })}
          >
            {drillInContent}
          </div>
        </div>
      </div>
    </HeaderContext.Provider>
  );
};

export default ContentDrill;
